import { getListingCountryFlag } from './countryFlags';

/**
 * In-memory cache for country flag data
 * Cache structure: Map<cacheKey, flagData>
 * cacheKey can be: address string, or geolocation "lat,lng"
 */
const flagCache = new Map();

// Cache statistics for debugging
const cacheStats = {
  hits: 0,
  misses: 0,
  size: 0,
};

/**
 * Generate a cache key from listing location data
 * @param {Object} listing - Listing object
 * @returns {string|null} Cache key or null if no location data
 */
const generateCacheKey = listing => {
  if (!listing || !listing.attributes) {
    return null;
  }

  const { publicData, geolocation } = listing.attributes;
  const address = publicData?.location?.address;

  // Prefer address-based key (more reliable)
  if (address && typeof address === 'string') {
    return `addr:${address.toLowerCase().trim()}`;
  }

  // Fallback to geolocation-based key
  if (geolocation && typeof geolocation.lat === 'number' && typeof geolocation.lng === 'number') {
    // Round to 2 decimal places for cache key (approx 1km precision)
    const lat = geolocation.lat.toFixed(2);
    const lng = geolocation.lng.toFixed(2);
    return `geo:${lat},${lng}`;
  }

  return null;
};

/**
 * Get cached country flag data for a listing
 * If not in cache, fetches from API and caches the result
 *
 * @param {Object} listing - Listing object
 * @returns {Promise<Object|null>} Promise that resolves to flag data or null
 */
export const getCachedCountryFlag = async listing => {
  const cacheKey = generateCacheKey(listing);

  if (!cacheKey) {
    return null;
  }

  // Check cache first
  if (flagCache.has(cacheKey)) {
    cacheStats.hits++;
    return flagCache.get(cacheKey);
  }

  // Cache miss - fetch from API
  cacheStats.misses++;

  try {
    const flagData = await getListingCountryFlag(listing);

    // Cache the result (even if null to avoid repeated failed lookups)
    flagCache.set(cacheKey, flagData);
    cacheStats.size = flagCache.size;

    return flagData;
  } catch (error) {
    console.error('Error fetching country flag:', error);
    // Cache null result to prevent repeated failures
    flagCache.set(cacheKey, null);
    return null;
  }
};

/**
 * Clear the entire flag cache
 * Useful for testing or when cache gets too large
 */
export const clearFlagCache = () => {
  flagCache.clear();
  cacheStats.hits = 0;
  cacheStats.misses = 0;
  cacheStats.size = 0;
};

/**
 * Get cache statistics for debugging
 * @returns {Object} Cache statistics
 */
export const getFlagCacheStats = () => {
  const hitRate = cacheStats.hits + cacheStats.misses > 0
    ? ((cacheStats.hits / (cacheStats.hits + cacheStats.misses)) * 100).toFixed(1)
    : 0;

  return {
    ...cacheStats,
    hitRate: `${hitRate}%`,
  };
};

/**
 * Remove old entries from cache if it exceeds max size
 * Simple LRU-like implementation
 *
 * @param {number} maxSize - Maximum cache size (default: 1000 entries)
 */
export const pruneFlagCache = (maxSize = 1000) => {
  if (flagCache.size > maxSize) {
    // Remove oldest 20% of entries
    const entriesToRemove = Math.floor(flagCache.size * 0.2);
    const keys = Array.from(flagCache.keys());

    for (let i = 0; i < entriesToRemove; i++) {
      flagCache.delete(keys[i]);
    }

    cacheStats.size = flagCache.size;
  }
};

// Export the original function as well for backwards compatibility
export { getListingCountryFlag } from './countryFlags';
